import { useState, useCallback, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useSimulatorCalculation = (formData, debouncedFormData, getAuthHeaders, reverseMode, desiredNetMonthly, debouncedDesiredNet, userEmail, consultantName, consultantNif, consultantBirthDate, consultantFunction, consultantEmail) => {
  const [loading, setLoading] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [results, setResults] = useState(null);

  const buildPayload = useCallback(() => {
    const grossValue = parseFloat(formData.gross_value);
    const permSalary = parseFloat(formData.perm_candidate_salary);
    const dailyRate = parseFloat(formData.daily_rate);

    return {
      ...formData,
      gross_value: grossValue || 0,
      hours_per_month: parseFloat(formData.hours_per_month) || 168,
      hours_per_day: parseFloat(formData.hours_per_day) || 8,
      auto_hours_per_month: formData.auto_hours_per_month !== false,
      fix_fee_12_months: formData.fix_fee_12_months !== false,
      dependents: parseInt(formData.dependents) || 0,
      meal_allowance_daily: parseFloat(formData.meal_allowance_daily),
      meal_allowance_card: formData.meal_allowance_card,
      meal_allowance_months: parseInt(formData.meal_allowance_months) || 11,
      include_health_insurance: formData.include_health_insurance,
      health_insurance_cost: parseFloat(formData.health_insurance_cost) || 0,
      health_insurance_dependents: parseInt(formData.health_insurance_dependents) || 0,
      consultant_age: formData.consultant_age != null && formData.consultant_age !== "" ? parseInt(formData.consultant_age) : null,
      dependents_ages: Array.isArray(formData.dependents_ages) ? formData.dependents_ages.map(a => {
        const p = parseInt(a);
        // Só envia idades identificadas (>0). Vazio/null/0 → null → backend trata como sem-idade → custo 0€.
        return Number.isFinite(p) && p > 0 ? p : null;
      }) : [],
      km_allowance: parseFloat(formData.km_allowance) || 0,
      km_allowance_months: parseInt(formData.km_allowance_months) || 12,
      insurance_capitalization: parseFloat(formData.insurance_capitalization) || 0,
      insurance_capitalization_months: parseInt(formData.insurance_capitalization_months) || 12,
      ticket_infancia: parseFloat(formData.ticket_infancia) || 0,
      ticket_infancia_months: parseInt(formData.ticket_infancia_months) || 12,
      coverflex: parseFloat(formData.coverflex) || 0,
      coverflex_months: parseInt(formData.coverflex_months) || 12,
      client_rate: formData.client_rate ? parseFloat(formData.client_rate) : null,
      billable_months: parseFloat(formData.billable_months) || 11,
      payment_terms: parseInt(formData.payment_terms) || 30,
      // parseFloat + fallback 21.7 alinhado com o default do backend (Pydantic).
      // parseInt truncava 21.7 -> 21, causando divergência de meal allowance
      // (7,14€/mês) entre /simulator e /simulator/shared/{id}. Ver iteration_63.
      working_days_month: parseFloat(formData.working_days_month) || 21.7,
      equipment_id: formData.equipment_id || null,
      equipment_ids: Array.isArray(formData.equipment_ids) ? formData.equipment_ids : [],
      license_id: formData.license_id || null,
      client_special_fee_id: formData.client_special_fee_id || null,
      is_fix_fee: !!formData.is_fix_fee,
      fix_fee_value: parseFloat(formData.fix_fee_value) || 0,
      vacation_days: parseInt(formData.vacation_days) || 22,
      daily_rate: parseFloat(formData.daily_rate) || 0,
      first_year_ss_exempt: formData.first_year_ss_exempt,
      include_agency_fee: !!formData.include_agency_fee,
      include_manual_premium: !!formData.include_manual_premium,
      manual_premium_value: parseFloat(formData.manual_premium_value) || 0,
      exclude_auto_premium: !!formData.exclude_auto_premium,
      // `payment_months` (meses em que o freelancer é pago) é INDEPENDENTE do
      // `billable_months` (meses que o cliente factura à empresa). O widget
      // PROPOSTA controla o payment_months (11 ou 12 no toggle) e o widget
      // NEGÓCIO controla o billable_months (aceita decimais 10, 10.5, 10.75).
      payment_months: parseInt(formData.payment_months) || 11,
      perm_candidate_salary: permSalary || 0,
      perm_percentage_fee: parseFloat(formData.perm_percentage_fee) || 0,
      perm_fixed_fee: parseFloat(formData.perm_fixed_fee) || 0,
      irs_jovem_year: parseInt(formData.irs_jovem_year) || 1,
      selected_benefits: formData.selected_benefits || [],
      user_email: userEmail || null,
      consultant_function: consultantFunction || null,
    };
  }, [formData, consultantFunction]);

  const [lastSavedId, setLastSavedId] = useState(null);

  const calculateSimulation = useCallback(async (silent = false) => {
    let hasValidInput = false;
    if (formData.business_type === "perm") {
      hasValidInput = parseFloat(formData.perm_candidate_salary) > 0;
    } else if (formData.regime_type === "green_receipts" || formData.regime_type === "b2b") {
      hasValidInput = parseFloat(formData.daily_rate) > 0;
    } else {
      hasValidInput = parseFloat(formData.gross_value) > 0 || parseFloat(formData.client_rate) > 0;
    }

    if (!hasValidInput) {
      if (!silent) toast.error("Introduza um valor válido");
      return;
    }

    if (!silent) { setLoading(true); setIsCalculating(true); }

    try {
      const payload = buildPayload();
      const response = await axios.post(`${API}/simulator/calculate`, payload, { headers: getAuthHeaders() });
      setResults(response.data);
      // NÃO há mais auto-save. Guardar só acontece via botão explícito
      // (Partilhar / PDF em ShareWidget → handleSaveAndShare).
      // Continuamos a validar margem para dar feedback ao user.
      const firstMargin = response.data?.results?.[0]?.margin_percentage;
      const hasNegativeMargin = typeof firstMargin === "number" && firstMargin < 0;
      if (!silent) {
        if (hasNegativeMargin) {
          toast.error(
            `Simulação calculada mas não guardada — margem negativa (${firstMargin.toFixed(1)}%). Ajusta os valores até obteres margem ≥ 0%.`,
            { duration: 6000 }
          );
        } else {
          toast.success("Simulação calculada!");
        }
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao calcular simulação";
      if (!silent) toast.error(message);
    } finally {
      if (!silent) { setLoading(false); setIsCalculating(false); }
    }
  }, [formData, getAuthHeaders, buildPayload]);

  const calculateReverse = useCallback(async (silent = false) => {
    const netValue = parseFloat(desiredNetMonthly);
    if (!netValue || netValue <= 0) {
      if (!silent) toast.error("Introduza o líquido desejado");
      return;
    }

    if (!silent) { setLoading(true); setIsCalculating(true); }

    try {
      const payload = {
        ...buildPayload(),
        desired_net_monthly: netValue,
      };
      const response = await axios.post(`${API}/simulator/reverse-calculate`, payload, { headers: getAuthHeaders() });
      setResults(response.data);
      if (!silent) toast.success("Cálculo inverso concluído!");
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao calcular inversamente";
      if (!silent) toast.error(message);
    } finally {
      if (!silent) { setLoading(false); setIsCalculating(false); }
    }
  }, [formData, desiredNetMonthly, getAuthHeaders, buildPayload]);

  // Auto-calculation on form change
  useEffect(() => {
    if (reverseMode) {
      const netValue = parseFloat(desiredNetMonthly);
      if (netValue > 0) calculateReverse(true);
      return;
    }
    let canCalculate = false;
    if (formData.business_type === "perm") {
      canCalculate = parseFloat(formData.perm_candidate_salary) > 0;
    } else if (formData.regime_type === "green_receipts" || formData.regime_type === "b2b") {
      canCalculate = parseFloat(formData.daily_rate) > 0;
    } else {
      canCalculate = parseFloat(formData.gross_value) > 0 || parseFloat(formData.client_rate) > 0;
    }
    if (canCalculate) calculateSimulation(true);
  }, [debouncedFormData, debouncedDesiredNet]);

  const result = results?.results?.[0];

  return {
    loading, isCalculating, results, result, lastSavedId, setResults,
    calculateSimulation, calculateReverse,
  };
};
